// Shared auth session — AuthProvider owns status once; useAuth() only consumes it.
// Fixes App + LoginPage each holding a separate authStatus (login bounce to /login).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useShellStore, type AuthStatus } from '../../../shell/store/shellStore';
import { getAuthAdapter, getBffAdapter } from '../../../adapters';
import type { AuthFlow } from '../../../adapters/auth/AuthAdapter';
import { AuthRefreshError } from '../../../adapters/auth/AuthRefreshError';
import { config } from '../../../config';
import { AUTH_ACCESS_TOKEN_KEY, AUTH_LOGOUT_FLAG_KEY } from '../../../constants/storageKeys';
import { ROUTE_LOGIN } from '../../../constants/routes';
import { fetchPermissionsManifest, fetchPersonalization } from '../services/authApi';
import { mintDevBypassAccessToken } from '../utils/devBypassToken';
import type { User } from '@growthaxl/web-foundation';

export type { AuthStatus };

export interface AuthContextValue {
  user: User | null;
  authStatus: AuthStatus;
  isLoggedIn: boolean;
  authFlow: AuthFlow;
  login: () => Promise<void>;
  completeLogin: (params: URLSearchParams) => Promise<void>;
  loginWithCredentials: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const MOCK_USER = {
  id: 'dev-user',
  name: 'Dev User',
  email: 'dev@growthaxl.com',
  roles: ['admin'] as string[],
};

/** Deduplicate boot across App / LoginSuccessPage / Layout useAuth() mounts. */
let authBootPromise: Promise<void> | null = null;

/** Test-only: allow each test to re-run the boot sequence. */
export function __resetAuthBootForTests(): void {
  authBootPromise = null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const user = useShellStore(state => state.user);
  const authStatus = useShellStore(state => state.authStatus);
  const setAuth = useShellStore(state => state.setAuth);
  const setAuthStatus = useShellStore(state => state.setAuthStatus);
  const setPersonalization = useShellStore(state => state.setPersonalization);
  const reset = useShellStore(state => state.reset);

  const auth = getAuthAdapter();
  const authFlow: AuthFlow = auth.flow;

  const hydrateSession = useCallback(async () => {
    const identity = auth.getIdentity();
    if (!identity) throw new Error('No identity after authentication');

    const [permissions, personalization] = await Promise.all([
      fetchPermissionsManifest().catch(() => null),
      fetchPersonalization().catch(() => null),
    ]);
    setAuth(identity.user, identity.tenant, permissions);
    setPersonalization(personalization);
  }, [auth, setAuth, setPersonalization]);

  const forceLogoutCallback = useCallback(() => {
    console.info('[auth] force-logout triggered');
    auth.clearSession();
    reset();
    window.location.href = ROUTE_LOGIN;
  }, [auth, reset]);

  useEffect(() => {
    // Always keep the latest force-logout handler on the BFF adapter.
    getBffAdapter().setForceLogoutHandler?.(forceLogoutCallback);

    if (!authBootPromise) {
      authBootPromise = (async () => {
        console.info('[auth] initializing auth status');
        setAuthStatus('initializing');

        const uiEnv = window.RUNTIME_CONFIG?.UI_ENV;
        const isProduction = uiEnv === 'production';
        const explicitBypass = window.RUNTIME_CONFIG?.UI_AUTH_BYPASS === 'true';
        const isLocal = uiEnv === 'local';

        if (explicitBypass && isProduction) {
          console.error('[auth] UI_AUTH_BYPASS=true is not permitted in production — ignored');
        }

        const isBypassRequested = !isProduction && (isLocal || explicitBypass);

        if (isBypassRequested) {
          console.warn('[auth] DEV BYPASS active — injecting mock user + SSE access_token');
          const mockTenantId = config.tenant || 'dev';
          const mockTenant = { id: mockTenantId, name: mockTenantId };
          // EventSource needs access_token query param; REST also uses getAccessToken().
          sessionStorage.setItem(
            AUTH_ACCESS_TOKEN_KEY,
            mintDevBypassAccessToken({
              userId: MOCK_USER.id,
              tenantId: mockTenantId,
              name: MOCK_USER.name,
              email: MOCK_USER.email,
              roles: MOCK_USER.roles,
            }),
          );
          setAuth(MOCK_USER, mockTenant, null);
          setAuthStatus('authenticated');
          return;
        }

        if (auth.isAuthenticated()) {
          try {
            await hydrateSession();
            setAuthStatus('authenticated');
          } catch {
            if (!auth.isAuthenticated()) {
              setAuthStatus('unauthenticated');
            }
          }
          return;
        }

        if (auth.getAccessToken() !== null) {
          try {
            await auth.refreshToken();
            await hydrateSession();
            setAuthStatus('authenticated');
          } catch (err) {
            if (err instanceof AuthRefreshError) {
              auth.clearSession();
            }
            if (!auth.isAuthenticated()) {
              setAuthStatus('unauthenticated');
            }
          }
          return;
        }

        if (auth.isAuthenticated()) {
          try {
            await hydrateSession();
            setAuthStatus('authenticated');
          } catch {
            setAuthStatus('unauthenticated');
          }
          return;
        }

        setAuthStatus('unauthenticated');
      })();
    }
  }, [auth, forceLogoutCallback, hydrateSession, setAuth, setAuthStatus]);

  const login = useCallback(async () => {
    window.location.href = await auth.buildLoginUrl(config.redirectUri);
  }, [auth]);

  const completeLogin = useCallback(
    async (params: URLSearchParams) => {
      await auth.handleCallback(params, config.redirectUri);
      await hydrateSession();
      setAuthStatus('authenticated');
    },
    [auth, hydrateSession, setAuthStatus]
  );

  const loginWithCredentials = useCallback(
    async (username: string, password: string) => {
      await auth.loginWithCredentials(username, password);
      await hydrateSession();
      setAuthStatus('authenticated');
    },
    [auth, hydrateSession, setAuthStatus]
  );

  const logout = useCallback(async () => {
    const logoutRedirectUrl = await auth.logout();
    reset();
    setAuthStatus('unauthenticated');

    if (logoutRedirectUrl) {
      const postLogoutUri = `${window.location.origin}${ROUTE_LOGIN}`;
      window.location.href = `${logoutRedirectUrl}&post_logout_redirect_uri=${encodeURIComponent(postLogoutUri)}`;
    } else {
      // Flag so LoginPage skips the auto-OIDC redirect once and shows
      // the login UI instead of instantly re-authenticating via SSO.
      sessionStorage.setItem(AUTH_LOGOUT_FLAG_KEY, '1');
      window.location.href = ROUTE_LOGIN;
    }
  }, [auth, reset, setAuthStatus]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      authStatus,
      isLoggedIn: authStatus === 'authenticated',
      authFlow,
      login,
      completeLogin,
      loginWithCredentials,
      logout,
    }),
    [user, authStatus, authFlow, login, completeLogin, loginWithCredentials, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used within <AuthProvider>');
  }
  return ctx;
}
